/**
 * Mitarbeiterfoto in der E-Mail-Signatur (Kundenfeedback 16.10.2026 Teil 2,
 * XXXII). Backend: /protected/users/me/signature-photo.
 *
 * Das Foto ist das Profilbild, serverseitig auf max. 240×240 px (JPEG)
 * verkleinert und über eine öffentliche, nicht erratbare URL erreichbar.
 * Mails betten nur diese URL ein – die Datenmenge je Mail bleibt gleich.
 *
 * Der Zustand ist modulweit geteilt: Profilseite (Schalter + Vorschau) und
 * Composer (Signatur beim Versand) sehen denselben Stand.
 */
import { reactive } from 'vue'
import { API_BASE_URL } from '@/config'

export interface SignaturePhotoInfo {
  hasPhoto: boolean
  enabled: boolean
  url: string
  hasProfileImage: boolean
}

const EMPTY: SignaturePhotoInfo = { hasPhoto: false, enabled: false, url: '', hasProfileImage: false }

const state = reactive<{ info: SignaturePhotoInfo, loaded: boolean }>({ info: { ...EMPTY }, loaded: false })
let pending: Promise<SignaturePhotoInfo> | null = null

const URL = `${API_BASE_URL}/protected/users/me/signature-photo`

function sanitize (data: Partial<SignaturePhotoInfo> | null | undefined): SignaturePhotoInfo {
  return {
    hasPhoto: data?.hasPhoto === true,
    enabled: data?.enabled === true,
    url: typeof data?.url === 'string' ? data.url : '',
    hasProfileImage: data?.hasProfileImage === true,
  }
}

/** Lädt den Stand einmal je Sitzung; Fehler (z. B. altes Backend) ergeben „kein Foto". */
export function loadSignaturePhoto (force = false): Promise<SignaturePhotoInfo> {
  if (state.loaded && !force) return Promise.resolve(state.info)
  if (pending && !force) return pending
  pending = fetch(URL, { credentials: 'include' })
    .then(res => (res.ok ? res.json() : null))
    .catch(() => null)
    .then((data) => {
      state.info = sanitize(data?.data ?? data)
      state.loaded = true
      pending = null
      return state.info
    })
  return pending
}

/** Schaltet „Foto in Signatur anzeigen"; wirft mit Meldung, wenn das Backend ablehnt. */
export async function saveSignaturePhotoEnabled (enabled: boolean): Promise<SignaturePhotoInfo> {
  const res = await fetch(URL, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  }).catch(() => null)
  if (!res) throw new Error('Einstellung konnte nicht gespeichert werden')
  const data = await res.json().catch(() => null)
  if (!res.ok) throw new Error(data?.message || 'Einstellung konnte nicht gespeichert werden')
  state.info = sanitize(data?.data ?? data)
  state.loaded = true
  return state.info
}

/** URL des Fotos für die Signatur – leer, wenn aus oder kein Foto. */
export function activeSignaturePhotoUrl (info: SignaturePhotoInfo | null | undefined): string {
  return info?.enabled && info?.hasPhoto && info?.url ? info.url : ''
}

export function useSignaturePhoto () {
  return state
}
